import { useEffect, type RefObject } from 'react';

const DRAG_THRESHOLD_PX = 5;

type Params = {
  containerRef: RefObject<HTMLDivElement | null>;
};

export function useHorizontalDragScroll({ containerRef }: Params): void {
  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    let isPointerDown = false;
    let startX = 0;
    let startScrollLeft = 0;
    let hasMoved = false;

    const resetCursor = () => {
      container.classList.remove('cursor-grabbing');
      container.classList.add('cursor-grab');
    };

    const handlePointerDown = (event: PointerEvent) => {
      if (event.button !== 0 || event.pointerType === 'touch') return;

      isPointerDown = true;
      hasMoved = false;
      startX = event.clientX;
      startScrollLeft = container.scrollLeft;
    };

    const handlePointerMove = (event: PointerEvent) => {
      if (!isPointerDown) return;

      const deltaX = event.clientX - startX;
      if (!hasMoved && Math.abs(deltaX) < DRAG_THRESHOLD_PX) return;

      if (!hasMoved) {
        hasMoved = true;
        container.setPointerCapture(event.pointerId);
        container.classList.remove('cursor-grab');
        container.classList.add('cursor-grabbing');
      }

      container.scrollLeft = startScrollLeft - deltaX;
    };

    const handlePointerUp = (event: PointerEvent) => {
      if (!isPointerDown) return;

      isPointerDown = false;

      if (container.hasPointerCapture(event.pointerId)) {
        container.releasePointerCapture(event.pointerId);
      }

      if (hasMoved) {
        const preventClick = (clickEvent: MouseEvent) => {
          clickEvent.preventDefault();
          clickEvent.stopImmediatePropagation();
        };

        container.addEventListener('click', preventClick, {
          capture: true,
          once: true,
        });
      }

      resetCursor();
    };

    container.addEventListener('pointerdown', handlePointerDown);
    container.addEventListener('pointermove', handlePointerMove);
    container.addEventListener('pointerup', handlePointerUp);
    container.addEventListener('pointercancel', handlePointerUp);

    return () => {
      container.removeEventListener('pointerdown', handlePointerDown);
      container.removeEventListener('pointermove', handlePointerMove);
      container.removeEventListener('pointerup', handlePointerUp);
      container.removeEventListener('pointercancel', handlePointerUp);
    };
  }, [containerRef]);
}
